'use client'

import { useState, useEffect } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { AuthInterceptor } from '@/lib/auth-interceptor'
import AuthManager from '@/lib/auth-manager'

// Cache session in memory to avoid repeated auth checks
let cachedSession: Session | null = null
let sessionPromise: Promise<Session | null> | null = null

export function useAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true

    const initAuth = async () => {
      try {
        // Use cached session if available and not expired
        if (cachedSession && cachedSession.expires_at && cachedSession.expires_at > Date.now() / 1000) {
          if (mounted) {
            setSession(cachedSession)
            setUser(cachedSession.user)
            setLoading(false)
          }
          return
        }

        // If there's already a session fetch in progress, wait for it
        if (sessionPromise) {
          const existingSession = await sessionPromise
          if (mounted) {
            setSession(existingSession)
            setUser(existingSession?.user ?? null)
            setLoading(false)
          }
          return
        }

        // Otherwise, fetch the session from our custom storage
        const currentPromise = new Promise<Session | null>((resolve) => {
          try {
            // Check if we're in browser environment
            if (typeof window === 'undefined') {
              resolve(null)
              return
            }

            const sessionData = localStorage.getItem('supreme_session')
            if (!sessionData) {
              cachedSession = null
              resolve(null)
              return
            }

            const session = JSON.parse(sessionData)

            // Check if session is expired
            if (session.expires_at && session.expires_at <= Date.now() / 1000) {
              localStorage.removeItem('supreme_session')
              localStorage.removeItem('auth_token')
              localStorage.removeItem('refresh_token')
              localStorage.removeItem('user_data')
              localStorage.removeItem('session_expires_at')
              cachedSession = null
              resolve(null)
              return
            }

            // Convert to Supabase-compatible format
            const supabaseSession = {
              access_token: session.access_token,
              refresh_token: session.refresh_token,
              expires_at: session.expires_at,
              user: session.user
            }

            // CRITICAL: Synchronize with Supabase client so getUser() works elsewhere
            supabase.auth.setSession({
              access_token: session.access_token,
              refresh_token: session.refresh_token
            }).then(({ error }) => {
              if (error) {
                console.warn('Failed to sync session with Supabase client:', error.message)
              }
            }).catch(err => {
              console.warn('setSession error:', err)
            })

            cachedSession = supabaseSession as Session
            resolve(supabaseSession as Session)
          } catch (error) {
            console.error('Session fetch error:', error)
            cachedSession = null
            resolve(null)
          }
        })

        // Store promise reference for concurrent calls to await
        sessionPromise = currentPromise

        const newSession = await currentPromise

        // Clear promise after resolution
        sessionPromise = null
        if (mounted) {
          setSession(newSession)
          setUser(newSession?.user ?? null)
          setLoading(false)
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
        if (mounted) {
          setLoading(false)
        }
      }
    }

    initAuth()

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Only update if we don't have a localStorage session or if this is a token refresh
      const localSession = localStorage.getItem('supreme_session')
      
      if (!localSession || event === 'TOKEN_REFRESHED') {
        cachedSession = session
        if (mounted) {
          setSession(session)
          setUser(session?.user ?? null)
          setLoading(false)
          
          // Reset auth interceptor retry count on successful refresh
          if (event === 'TOKEN_REFRESHED') {
            AuthInterceptor.resetRetryCount()
          }
        }
      }
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  const signUp = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    })
    return { data, error }
  }

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    
    // Initialize auth manager after successful sign in
    if (data?.session) {
      const authManager = AuthManager.getInstance()
      // Start token monitoring (automatic refresh before expiry)
      authManager.getTimeUntilExpiry().then(seconds => {
        if (seconds) {
          console.log(`New session: Access token will expire in ${Math.floor(seconds / 60)} minutes`)
        }
      })
    }
    
    return { data, error }
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    return { error }
  }

  const getSession = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    return session
  }

  return {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    getSession,
  }
}